import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sparkling-line text-[#1B3D2A]" />
          </div>
        </div>
        <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-[#5A6B5A]">{subtitle}</p>}
      <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : delay === 1 ? 'scroll-reveal-delay-1' : delay === 2 ? 'scroll-reveal-delay-2' : delay === 3 ? 'scroll-reveal-delay-3' : delay === 4 ? 'scroll-reveal-delay-4' : delay === 5 ? 'scroll-reveal-delay-5' : 'scroll-reveal-delay-6';
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  category: string;
  icon: string;
  items: FaqItem[];
}

const faqCategories: FaqCategory[] = [
  {
    category: '聽力損失基礎',
    icon: 'ri-volume-up-line',
    items: [
      {
        question: '什麼是聽力損失？',
        answer: '聽力損失是指聽力下降的程度，無法聽到正常音量範圍內的聲音。聽力損失可分為傳導性、感音神經性及混合性三種。傳導性聽損是外耳或中耳問題，感音神經性聽損是內耳或聽神經受損，混合性則兩者兼有。',
      },
      {
        question: '聽力損失有哪些早期徵兆？',
        answer: '早期徵兆包括：在吵雜環境中難以聽清對話、經常要求對方重複說話、電視音量調得比家人高、聽不到門鈴或電話聲、覺得女性或小孩聲音聽不清楚、耳鳴持續存在。這些症狀容易被忽略，建議定期進行聽力篩檢。',
      },
      {
        question: '聽力損失的程度如何分級？',
        answer: '聽力損失依純音聽力檢查的平均聽力閾值分級：正常（0-25 dB）、輕度（26-40 dB）、中度（41-55 dB）、中重度（56-70 dB）、重度（71-90 dB）及極重度（超過 90 dB）。輕度聽損者通常已經在日常生活中感到溝通困難。',
      },
    ],
  },
  {
    category: '助聽器相關',
    icon: 'ri-headphone-line',
    items: [
      {
        question: '什麼時候需要配戴助聽器？',
        answer: '當聽力損失影響到日常生活溝通時，就建議考慮助聽器。一般來說，輕度聽損（26-40 dB）若已造成溝通困擾，助聽器可以帶來幫助；中度以上聽損則強烈建議使用。助聽器選配需要經由專業聽力師評估後進行。',
      },
      {
        question: '助聽器會不會越戴越聾？',
        answer: '不會。助聽器的主要功能是放大聲音，讓使用者聽得更清楚，並不會進一步損害聽力。相反地，研究顯示長期未使用助聽器的聽損者，因聽覺刺激不足，大腦語言處理能力反而會加速退化。及早配戴助聽器有助於維持聽覺與認知功能。',
      },
      {
        question: '助聽器需要多長時間適應？',
        answer: '適應期因人而異，通常需要 4-12 週。初期可能會覺得聲音太吵或太尖，這是正常的。建議從安靜環境開始，每天配戴 2-4 小時，逐步增加時間。富聆聽的聽力師會在適應期間提供多次調校服務，確保最佳效果。',
      },
      {
        question: '助聽器可以戴著洗澡或游泳嗎？',
        answer: '一般助聽器不防水，不建議戴著洗澡或游泳。市面上有防水等級較高的助聽器型號，但使用前仍需確認規格。日常保養時，應避免讓助聽器接觸水氣、汗水或濕氣過重的環境。建議每天使用除濕盒或電子乾燥器進行保養。',
      },
    ],
  },
  {
    category: '聽力檢測',
    icon: 'ri-stethoscope-line',
    items: [
      {
        question: '聽力檢測需要多長時間？',
        answer: '完整的聽力檢測約需 30-60 分鐘，包括病史詢問、耳鏡檢查、純音聽力檢查、語音辨識測試等。富聆聽提供免費基礎聽力篩檢，約 15-20 分鐘即可完成，適合初步了解自己的聽力狀況。',
      },
      {
        question: '聽力檢測會不會痛？',
        answer: '不會。聽力檢測是無痛的非侵入性檢查。您只需要坐在隔音室內，透過耳機聽到不同頻率與音量的聲音，聽到就按鈕即可。整個過程輕鬆舒適，不會造成任何身體不適。',
      },
      {
        question: '多久應該做一次聽力檢查？',
        answer: '建議 40 歲以上成年人每年檢查一次；65 歲以上長者建議每半年檢查。若有糖尿病、高血壓等慢性病，或長期處於噪音環境，應每半年至一年檢查。兒童建議在出生後、學齡前及入學後定期進行聽力篩檢。',
      },
    ],
  },
  {
    category: '聽力與慢性病',
    icon: 'ri-heart-pulse-line',
    items: [
      {
        question: '糖尿病會影響聽力嗎？',
        answer: '會。糖尿病患者發生聽力損失的風險是正常人的 2.3 倍。長期高血糖會損傷內耳微血管與神經纖維，導致漸進性感音神經性聽損。美國糖尿病學會已將聽力篩檢納入糖友年度檢查建議項目。',
      },
      {
        question: '高血壓與聽力損失有什麼關係？',
        answer: '高血壓會減少內耳血液灌注，加速聽力衰退。研究顯示，收縮壓每升高 10 mmHg，聽損風險增加約 6%。內耳對血流變化極為敏感，因此穩定控制血壓是預防聽力退化的重要措施。',
      },
      {
        question: '聽力損失會增加失智風險嗎？',
        answer: '是的。約翰霍普金斯大學研究發現，重度聽損者認知退化速度比常人快 30-40%，失智風險增加 3.2 倍。這是因為聽覺刺激不足會導致大腦語言處理區域萎縮。早期配戴助聽器可以顯著減緩這一風險。',
      },
    ],
  },
  {
    category: '兒童與銀髮聽力',
    icon: 'ri-parent-line',
    items: [
      {
        question: '如何觀察孩子是否有聽力問題？',
        answer: '嬰幼兒聽力問題的徵兆包括：對聲音沒有反應、不會尋找聲源、語言發展比同齡孩子慢、說話不清楚。學齡兒童可能表現為：上課聽不清楚、成績突然下滑、經常要求重複說話。建議及早帶至耳鼻喉科或聽力中心檢查。',
      },
      {
        question: '長者聽力退化是正常的嗎？',
        answer: '聽力隨年齡增長確實會自然退化，稱為「老年性聽損」。但這不代表無法改善。現代助聽器技術已非常先進，可以有效補償老年性聽損。更重要的是，及早介入可以避免因聽力退化導致的社交孤立、憂鬱及認知退化。',
      },
    ],
  },
  {
    category: '費用與補助',
    icon: 'ri-money-cny-circle-line',
    items: [
      {
        question: '助聽器有政府補助嗎？',
        answer: '有。台灣各縣市政府對聽障者購買助聽器提供補助，一般為每耳 1,000-5,000 元不等，身障程度較重者補助額度較高。申請需經醫院評估並取得身心障礙證明。富聆聽的聽力師可協助您了解申請流程與所需文件。',
      },
      {
        question: '助聽器的價格範圍是多少？',
        answer: '助聽器價格依功能、品牌及技術等級而異，一般單耳價格從 15,000 元到 80,000 元不等。入門級適合輕度聽損，高階款則具備藍牙連線、降噪、AI 自動調節等先進功能。富聆聽提供多種價位選擇，並有分期付款方案。',
      },
    ],
  },
];

function buildFaqSchema() {
  const mainEntity = faqCategories.flatMap((cat) =>
    cat.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

export default function FaqPage() {
  const navigate = useNavigate();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  const toggleQuestion = (key: string) => {
    setOpenQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const faqSchema = buildFaqSchema();

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD FAQPage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ===== Hero ===== */}
        <section className="relative text-white pt-8 md:pt-14 pb-8 md:pb-14">
          {/* Decorative Layer (clipped) */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Solid Dark Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
            {/* Background Image - subtle texture only */}
            <div className="absolute inset-0 opacity-[0.06]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20inviting%20modern%20consultation%20room%20with%20soft%20lighting%20comfortable%20chairs%20and%20green%20botanical%20accents%20professional%20healthcare%20setting%20calm%20reassuring%20atmosphere%20minimalist%20interior%20design%20sage%20tones&width=1600&height=700&seq=faq-hero-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Subtle Geometric Pattern */}
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#D4A574_1px,_transparent_1px),radial-gradient(circle_at_80%_70%,_#D4A574_1px,_transparent_1px)] bg-[length:60px_60px,80px_80px]" />
            {/* Decorative Glow */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
            <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 md:gap-8">
              {/* Left: Title & Description */}
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-px h-5 bg-[#D4A574]" />
                  <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">
                    Frequently Asked Questions
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                  聽力常見
                  <span className="block text-[#D4A574] mt-2 md:mt-3">問題</span>
                </h1>
                <p className="mt-3 md:mt-4 text-white/88 text-base md:text-lg leading-relaxed max-w-xl">
                  彙整聽力保健、助聽器選配、聽力檢測及聽力與慢性病相關的常見問題，
                  由富聆聽專業聽力師團隊為您解答，讓您一次掌握聽力健康關鍵資訊。
                </p>
                {/* FAQ stats */}
                <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-question-answer-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">6 大主題分類</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">17 則常見問答</span>
                  </div>
                </div>
              </div>

              {/* Right: Search hint */}
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-search-line text-[#D4A574]" />
                    </div>
                  </div>
                  <span className="text-xs text-white/70 whitespace-nowrap">快速搜尋解答</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-customer-service-2-line text-[#D4A574]" />
                    </div>
                  </div>
                  <span className="text-xs text-white/70 whitespace-nowrap">專人諮詢服務</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white"/>
          </svg>
        </section>

        {/* ===== FAQ Categories ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Questions & Answers" title="常見問題分類" subtitle="點擊分類展開，找到您想了解的問題與解答" />

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
            {faqCategories.map((cat) => (
              <button
                type="button"
                key={cat.category}
                onClick={() => setOpenCategory(openCategory === cat.category ? null : cat.category)}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-full text-sm md:text-base font-medium transition-all whitespace-nowrap border flex items-center gap-2 ${
                  openCategory === cat.category
                    ? 'bg-[#1B3D2A] text-white border-[#1B3D2A]'
                    : 'bg-white text-[#5A6B5A] border-[#E8E0D6] hover:border-[#D4A574] hover:text-[#1B3D2A]'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`} />
                </div>
                {cat.category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4 md:space-y-6">
            {faqCategories.map((cat, catIdx) => (
              <SectionReveal key={cat.category} delay={catIdx}>
                <div
                  className={`bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden transition-all duration-300 ${
                    openCategory === cat.category ? 'border-[#D4A574]/40 shadow-lg' : 'hover:border-[#D4A574]/30'
                  }`}
                >
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => setOpenCategory(openCategory === cat.category ? null : cat.category)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                        openCategory === cat.category ? 'bg-[#1B3D2A]' : 'bg-[#1B3D2A]/10'
                      }`}>
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-lg ${openCategory === cat.category ? 'text-white' : 'text-[#1B3D2A]'}`} />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-base md:text-lg font-bold text-[#1B3D2A]">{cat.category}</h3>
                        <p className="text-sm text-[#9A8A7A]">{cat.items.length} 個問題</p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openCategory === cat.category ? 'bg-[#1B3D2A] text-white rotate-180' : 'bg-[#FAF8F5] text-[#9A8A7A]'
                    }`}>
                      <i className="ri-arrow-down-s-line text-lg" />
                    </div>
                  </button>

                  {/* Questions */}
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openCategory === cat.category ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 space-y-3">
                      {cat.items.map((item, qIdx) => {
                        const key = `${cat.category}-${qIdx}`;
                        const isOpen = openQuestions.has(key);
                        return (
                          <div
                            key={key}
                            className={`rounded-xl border transition-all duration-300 ${
                              isOpen ? 'border-[#D4A574]/40 bg-[#FAF8F5]' : 'border-[#E8E0D6]/60 bg-white'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleQuestion(key)}
                              className="w-full flex items-start gap-3 p-4 md:p-5 text-left"
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-300 ${
                                isOpen ? 'bg-[#1B3D2A]' : 'bg-[#1B3D2A]/10'
                              }`}>
                                <span className={`text-xs font-bold ${isOpen ? 'text-white' : 'text-[#1B3D2A]'}`}>
                                  {qIdx + 1}
                                </span>
                              </div>
                              <span className="text-sm md:text-base font-semibold text-[#1B3D2A] leading-snug flex-1">
                                {item.question}
                              </span>
                              <div className={`w-6 h-6 flex items-center justify-center shrink-0 mt-1 transition-transform duration-300 ${
                                isOpen ? 'rotate-180' : ''
                              }`}>
                                <i className="ri-arrow-down-s-line text-[#9A8A7A] text-lg" />
                              </div>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${
                              isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="px-4 md:px-5 pb-4 md:pb-5 pl-14 md:pl-16">
                                <p className="text-sm md:text-base text-[#5A6B5A] leading-relaxed">
                                  {item.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>

        {/* Wave: white to warm */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== CTA ===== */}
        <div className="bg-[#FAF8F5] py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionReveal>
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">專業諮詢</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                    還有其他問題？
                  </h3>
                  <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                    每個人的聽力狀況都不同，我們的專業聽力師樂意為您提供一對一的諮詢服務。歡迎預約免費聽力檢測，或透過聯絡我們獲得更詳細的解答。
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate('/booking')}
                    className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calendar-check-line" />
                    </div>
                    預約免費檢測
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3 rounded-xl text-sm font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line" />
                    </div>
                    聯絡我們
                  </button>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}